
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Message } from "element-ui";
import { IAddShippingModel, IFactoryLoadingPlan } from "@/api/types";
import { addFactoryLoadingPlanShippingOrder } from "@/api/factory-loading-plans";
import { getShipping } from "@/api/shipping";
import { COMPANY_OPTIONS } from "@/constant";
import {
  highlightKeywords,
  matchesAllKeywords,
} from "@/utils/keyword-search";

interface AddShippingForm {
  combinedContainer: boolean | null;
  shippingOrderId: string | null;
}

@Component({
  name: "AddShippingOrderDialog",
})
export default class AddShippingOrderDialog extends Vue {
  @Prop({ required: true }) private visible!: boolean;
  @Prop({ default: null }) private factoryLoadingPlan!: IFactoryLoadingPlan | null;

  private keyword = "";
  private listLoading = false;
  private submitting = false;
  private shippingOrderList: IAddShippingModel[] = [];
  private form: AddShippingForm = {
    combinedContainer: null,
    shippingOrderId: null,
  };

  get planCode(): string {
    return (this.factoryLoadingPlan && this.factoryLoadingPlan.code) || "-";
  }

  get customerAbbr(): string {
    return (
      (this.factoryLoadingPlan && this.factoryLoadingPlan.customerAbbr) || ""
    );
  }

  get companyLabel(): string {
    const code =
      this.factoryLoadingPlan && this.factoryLoadingPlan.companyCode;
    if (!code) return "-";
    return COMPANY_OPTIONS[code] || code;
  }

  get declarationLabel(): string {
    const plan = this.factoryLoadingPlan;
    if (!plan || plan.declaration == null) return "-";
    return plan.declaration ? "是" : "否";
  }

  get searchKeywords(): string[] {
    return (this.keyword || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
  }

  get filteredOrders(): IAddShippingModel[] {
    const keywords = this.searchKeywords.map((term) => term.toLowerCase());
    if (!keywords.length) return this.shippingOrderList;
    return this.shippingOrderList.filter((item) =>
      matchesAllKeywords([String(item.shippingCode || "")], keywords)
    );
  }

  get selectedOrder(): IAddShippingModel | null {
    if (!this.form.shippingOrderId) return null;
    return (
      this.shippingOrderList.find(
        (item) => item.id === this.form.shippingOrderId
      ) || null
    );
  }

  @Watch("visible")
  private onVisibleChange(val: boolean) {
    if (val) {
      this.resetForm();
    }
  }

  private resetForm() {
    this.keyword = "";
    this.shippingOrderList = [];
    const plan = this.factoryLoadingPlan;
    this.form = {
      combinedContainer:
        plan && plan.combinedContainer != null
          ? !!plan.combinedContainer
          : null,
      shippingOrderId: null,
    };
    this.loadShippingOrders();
  }

  private selectOrder(id: string) {
    this.form.shippingOrderId =
      this.form.shippingOrderId === id ? null : id;
  }

  private highlightShippingCode(text: string): string {
    return highlightKeywords(text, this.searchKeywords);
  }

  private async onCombinedChange(value: boolean) {
    this.form.combinedContainer = value;
    this.form.shippingOrderId = null;
    this.keyword = "";
    await this.loadShippingOrders();
  }

  private async loadShippingOrders() {
    const plan = this.factoryLoadingPlan;
    if (!plan || !plan.customerId || this.form.combinedContainer === null) {
      this.shippingOrderList = [];
      this.listLoading = false;
      return;
    }
    this.listLoading = true;
    try {
      const data: any = await getShipping({
        combinedContainer: this.form.combinedContainer,
        customerId: plan.customerId,
        company: plan.companyCode,
      });
      this.shippingOrderList = Array.isArray(data) ? data : [];
    } finally {
      this.listLoading = false;
    }
  }

  private handleClose() {
    this.$emit("update:visible", false);
  }

  private async handleSubmit() {
    const plan = this.factoryLoadingPlan;
    if (!plan || !plan.id) {
      Message.error("报关计划信息不完整");
      return;
    }
    if (this.form.combinedContainer === null) {
      Message.error("请选择是否拼柜");
      return;
    }
    if (!this.form.shippingOrderId) {
      Message.error("请选择订舱单");
      return;
    }

    this.submitting = true;
    try {
      await addFactoryLoadingPlanShippingOrder(plan.id, {
        id: plan.id,
        code: plan.code,
        customerId: plan.customerId,
        customerAbbreviation: plan.customerAbbr,
        companyCode: plan.companyCode,
        combinedContainer: this.form.combinedContainer,
        declaration: plan.declaration,
        shippingOrderId: this.form.shippingOrderId,
      });
      this.$notify({
        title: "成功",
        message: "舱单已添加",
        type: "success",
        duration: 2000,
      });
      this.$emit("success");
      this.handleClose();
    } finally {
      this.submitting = false;
    }
  }
}
